// The picking flow as a pure state machine.
//
// Every behaviour the specs constrain lives here rather than in the DOM layer:
// step gating, wall-clock preservation across a location change, the client-side
// booking window, the in-flight lock, the non-re-arm after success, and the
// three-way outcome branch. src/app.js only renders this and dispatches into it.

import { ACTIVITIES, CUSTOM_ACTIVITY_ID, findActivity, normaliseCustomName } from '../shared/catalog.js';
import { HOME_ZONE, LOCATIONS, findLocation } from '../shared/zones.js';
import { resolveWallClock } from '../shared/wallclock.js';
import { renderBothClocks } from '../shared/render.js';
import { Temporal } from 'temporal-polyfill';
import {
  CLIENT_MIN_LEAD_MINUTES,
  checkBookingWindow,
  firstSelectableDay,
  lastSelectableDay,
} from '../shared/booking.js';

/** How long the flow waits for an answer before calling the outcome unknown. */
export const IN_FLIGHT_TIMEOUT_MS = 30_000;

export const initialState = Object.freeze({
  /** She has not been asked yet. The picker is behind the asking. */
  accepted: false,
  locationId: null,
  activityId: null,
  /** What she wrote, when the chosen activity is the write-your-own one. */
  customActivity: '',
  date: null,
  time: null,
  /** 'editing' | 'inflight' | 'scheduled' | 'rejected' | 'unknown' */
  status: 'editing',
  answer: null,
  /** Identifies a selection already scheduled in this document, to block re-arm. */
  scheduledSignature: null,
  /** The event this booking replaces, if any. Set from the link she arrived on
   *  or from the change button on the already-booked panel. */
  replaceEventId: null,
  /** Which month the calendar is showing, "YYYY-MM". Null follows the range. */
  viewMonth: null,
});

const signature = (s) => [s.locationId, s.activityId, s.date, s.time].join('|');

/**
 * @param {object} state
 * @param {{type: string} & Record<string, unknown>} action
 * @returns {object} next state
 */
export function reduce(state, action) {
  switch (action.type) {
    case 'cancelReplace':
      return { ...state, replaceEventId: null, status: editingAgain(state) };

    case 'replaceEvent':
      // Rescheduling implies she has already been asked, so this also accepts.
      return { ...state, replaceEventId: action.eventId, accepted: true, status: editingAgain(state) };

    case 'accept':
      return { ...state, accepted: true };

    case 'selectLocation':
      // Her entered day and wall clock are preserved verbatim. Only the derived
      // instant changes — 21:00 in Korea becomes 21:00 in Seattle, not 05:00.
      return { ...state, locationId: action.locationId, status: editingAgain(state) };

    case 'setCustomActivity':
      return { ...state, customActivity: action.text, status: editingAgain(state) };

    case 'selectActivity':
      return { ...state, activityId: action.activityId, status: editingAgain(state) };

    case 'selectDay':
      return { ...state, date: action.date, status: editingAgain(state) };

    case 'selectTime':
      return { ...state, time: action.time, status: editingAgain(state) };

    case 'setHour': {
      const minute = state.time ? Number(state.time.slice(3, 5)) : 0;
      return { ...state, time: clock(action.hour, minute), status: editingAgain(state) };
    }

    case 'setMinute': {
      const hour = state.time ? Number(state.time.slice(0, 2)) : 20;
      return { ...state, time: clock(hour, action.minute), status: editingAgain(state) };
    }

    case 'setHour12': {
      const current = state.time ? Number(state.time.slice(0, 2)) : 20;
      const minute = state.time ? Number(state.time.slice(3, 5)) : 0;
      const { meridiem } = to12(current);
      return { ...state, time: clock(to24(action.hour12, meridiem), minute), status: editingAgain(state) };
    }

    case 'setMeridiem': {
      const current = state.time ? Number(state.time.slice(0, 2)) : 20;
      const minute = state.time ? Number(state.time.slice(3, 5)) : 0;
      const { hour12 } = to12(current);
      return { ...state, time: clock(to24(hour12, action.meridiem), minute), status: editingAgain(state) };
    }

    case 'nudgeMinute': {
      // Wraps inside the hour rather than rolling it over: predictable, and
      // the hour is one tap away if she wants it changed.
      if (!state.time) return state;
      const hour = Number(state.time.slice(0, 2));
      const minute = (Number(state.time.slice(3, 5)) + action.delta + 60) % 60;
      return { ...state, time: clock(hour, minute), status: editingAgain(state) };
    }

    case 'shiftMonth': {
      if (!state.viewMonth) return state;
      const shifted = Temporal.PlainYearMonth.from(state.viewMonth).add({ months: action.delta });
      return { ...state, viewMonth: shifted.toString() };
    }

    case 'setViewMonth':
      return { ...state, viewMonth: action.month };

    case 'submitRequested':
      // The lock: a submission already in flight, or an unchanged selection
      // already scheduled, does not start another.
      if (state.status === 'inflight') return state;
      if (state.scheduledSignature === signature(state)) return state;
      return { ...state, status: 'inflight', answer: null };

    case 'answerReceived': {
      if (state.status !== 'inflight') return state;
      const outcome = classifyAnswer(action.answer);
      return {
        ...state,
        status: outcome,
        answer: outcome === 'unknown' ? null : action.answer,
        scheduledSignature: outcome === 'scheduled' ? signature(state) : state.scheduledSignature,
      };
    }

    case 'requestFailed':
      // No answer arrived: a network failure or the in-flight timeout. Unknown,
      // never rejected — we cannot know whether the event was created.
      if (state.status !== 'inflight') return state;
      return { ...state, status: 'unknown', answer: null };

    case 'scheduleAnother':
      // The only explicit new action that re-arms submission.
      return { ...initialState, locationId: state.locationId };

    default:
      return state;
  }
}

const pad2 = (n) => String(n).padStart(2, '0');
const clock = (hour, minute) => `${pad2(hour)}:${pad2(minute)}`;

// The picker speaks 12-hour with a meridiem; everything underneath — storage,
// the request and the Discord event — stays 24-hour, because that is
// unambiguous and it is what the server and the spec expect.
export const to12 = (hour24) => ({
  hour12: hour24 % 12 === 0 ? 12 : hour24 % 12,
  meridiem: hour24 < 12 ? 'AM' : 'PM',
});
export const to24 = (hour12, meridiem) =>
  (hour12 % 12) + (meridiem === 'PM' ? 12 : 0);

/** Editing a field after an outcome returns the flow to editing. */
function editingAgain(state) {
  return state.status === 'inflight' ? 'inflight' : 'editing';
}

/**
 * Which of the three outcomes an answer names. Anything unrecognised — a body
 * without the field, an unknown value, a null — is `unknown`, never `rejected`.
 */
export function classifyAnswer(answer) {
  const outcome = answer && typeof answer === 'object' ? answer.outcome : undefined;
  if (outcome === 'scheduled') return 'scheduled';
  if (outcome === 'rejected') return 'rejected';
  return 'unknown';
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Messages for the refusal reasons the server can name. */
const REJECTION_MESSAGES = {
  unauthorized:
    'That link is missing its access credential. Use the full bookmarked link — retrying from this one will not work.',
  'unknown-activity':
    'That activity is no longer available. Reload the page to get the current list.',
  'too-soon': 'That time has already gone by. Pick a time from now on.',
  'too-far': 'That day is beyond how far ahead we can book.',
  'nonexistent-time': 'That time does not exist on that day, because the clocks move forward.',
  misconfigured: 'Something is misconfigured on my side. Not your fault — tell Jonathan.',
};

/**
 * Derive everything the view needs.
 * @param {object} state
 * @param {{now?: string}} [ctx]
 */
export function derive(state, ctx = {}) {
  const now = ctx.now ?? new Date().toISOString();
  const location = state.locationId ? findLocation(state.locationId) : { found: false };
  const zone = location.found ? location.location.zone : null;

  const view = {
    accepted: state.accepted,
    replaceEventId: state.replaceEventId,
    locations: LOCATIONS,
    activities: ACTIVITIES,
    homeZone: HOME_ZONE,
    locationId: state.locationId,
    activityId: state.activityId,
    customActivity: state.customActivity,
    /** True while the write-your-own entry is selected. */
    isCustom: state.activityId === CUSTOM_ACTIVITY_ID,
    date: state.date,
    time: state.time,
    status: state.status,
    /** Later steps are visible but inert until a location is chosen. */
    stepsEnabled: Boolean(zone),
    firstDay: zone ? firstSelectableDay(zone, now) : null,
    lastDay: zone ? lastSelectableDay(zone, now) : null,
    resolution: null,
    clocks: null,
    ambiguous: false,
    refusal: null,
    canSubmit: false,
    outcome: null,
    calendar: null,
    // Hour and minute the picker highlights. Null until she has set a time.
    timeParts: state.time
      ? {
          hour: Number(state.time.slice(0, 2)),
          minute: Number(state.time.slice(3, 5)),
          ...to12(Number(state.time.slice(0, 2))),
        }
      : null,
  };

  if (state.status === 'scheduled' && state.answer) {
    // The success screen shows the moment the SERVER used, not the preview.
    view.outcome = {
      kind: 'scheduled',
      instant: state.answer.instant,
      durationMinutes: state.answer.durationMinutes,
      announced: state.answer.announced !== false,
      ambiguous: Boolean(state.answer.ambiguous),
      eventUrl: state.answer.eventUrl,
      clocks: zone ? renderBothClocks(state.answer.instant, zone, HOME_ZONE) : null,
    };
    return view;
  }
  if (state.status === 'rejected') {
    const reason = state.answer?.reason ?? 'unknown-reason';
    view.outcome = {
      kind: 'rejected',
      reason,
      message: REJECTION_MESSAGES[reason]
        ?? 'The date night was not scheduled. Have another go.',
      retryable: reason !== 'unauthorized' && reason !== 'unknown-activity',
    };
    return view;
  }
  if (state.status === 'unknown') {
    view.outcome = {
      kind: 'unknown',
      message:
        'I did not hear back, so I cannot tell whether that was scheduled. Check the Discord channel before trying again.',
    };
    return view;
  }

  // The calendar grid. Built here rather than in the DOM layer so the range
  // rules live in one place with everything else that knows about her zone.
  //
  // It is built even before a location is chosen, so the step shows a real
  // month with every day inert — matching how the activity cards behave. An
  // empty placeholder box was the odd one out, and read as broken.
  {
    const layoutZone = zone ?? HOME_ZONE;
    const first = Temporal.PlainDate.from(
      zone ? view.firstDay : firstSelectableDay(layoutZone, now),
    );
    const last = Temporal.PlainDate.from(
      zone ? view.lastDay : lastSelectableDay(layoutZone, now),
    );
    // `first` already carries the no-location fallback; view.firstDay does not.
    const anchor = state.viewMonth
      ? Temporal.PlainYearMonth.from(state.viewMonth)
      : Temporal.PlainYearMonth.from(state.date ?? first.toString());

    const monthStart = anchor.toPlainDate({ day: 1 });
    // Grid starts on the Monday of the week containing the 1st.
    const gridStart = monthStart.subtract({ days: monthStart.dayOfWeek - 1 });

    const weeks = [];
    for (let w = 0; w < 6; w += 1) {
      const row = [];
      for (let d = 0; d < 7; d += 1) {
        const day = gridStart.add({ days: w * 7 + d });
        const iso = day.toString();
        row.push({
          iso,
          day: day.day,
          inMonth: day.month === anchor.month && day.year === anchor.year,
          selectable:
            Boolean(zone) &&
            Temporal.PlainDate.compare(day, first) >= 0 &&
            Temporal.PlainDate.compare(day, last) <= 0,
          selected: state.date === iso,
          isToday: Boolean(zone) && iso === view.firstDay,
        });
      }
      weeks.push(row);
    }

    view.calendar = {
      month: anchor.toString(),
      label: `${MONTHS[anchor.month - 1]} ${anchor.year}`,
      weekdays: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
      weeks,
      canGoBack: Boolean(zone) && Temporal.PlainDate.compare(monthStart, first) > 0,
      canGoForward:
        Boolean(zone) && Temporal.PlainDate.compare(monthStart.add({ months: 1 }), last) <= 0,
    };
  }

  if (!zone || !state.date || !state.time) return view;

  const [year, month, day] = state.date.split('-').map(Number);
  const [hour, minute] = state.time.split(':').map(Number);
  const resolved = resolveWallClock({ year, month, day, hour, minute }, zone);
  view.resolution = resolved;

  if (!resolved.ok) {
    view.refusal = resolved.reason === 'nonexistent'
      ? { kind: 'nonexistent', nearest: resolved.nearest }
      : { kind: 'invalid' };
    return view;
  }

  view.ambiguous = resolved.ambiguous;
  view.clocks = renderBothClocks(resolved.instant, zone, HOME_ZONE);

  const window = checkBookingWindow({
    instant: resolved.instant, zone, minLeadMinutes: CLIENT_MIN_LEAD_MINUTES, now,
  });
  if (!window.ok) {
    view.refusal = window.edge === 'too-soon'
      ? { kind: 'too-soon', minLeadMinutes: window.minLeadMinutes }
      : { kind: 'too-far', lastDay: window.lastDay };
    return view;
  }

  // A written-in activity is not complete until something has been written.
  const customOk = state.activityId !== CUSTOM_ACTIVITY_ID
    || normaliseCustomName(state.customActivity).ok;
  const complete = Boolean(
    state.locationId && state.activityId && state.date && state.time && customOk);
  const alreadyScheduled = state.scheduledSignature === signature(state);
  view.canSubmit = complete && state.status === 'editing' && !alreadyScheduled;
  view.activity = state.activityId ? findActivity(state.activityId) : null;

  return view;
}

/** The request body. The server derives everything else. */
export function requestBody(state) {
  const body = {
    locationId: state.locationId,
    activityId: state.activityId,
    date: state.date,
    time: state.time,
  };
  // Sent only for the write-your-own entry. For every other activity the
  // server takes the name from the catalog and ignores anything offered here.
  if (state.activityId === CUSTOM_ACTIVITY_ID) body.customActivity = state.customActivity;
  if (state.replaceEventId) body.replaceEventId = state.replaceEventId;
  return body;
}
